// lib
import { useContext, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import cookie from 'react-cookies'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'

import { toggleCheckbox } from '@aytm/app/stats/page/sidebar/components/lib/utils'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'
import useApplyFilters from '@aytm/app/stats/page/sidebar/components/hooks/useApplyFilters'

// components
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'
import { Checkbox } from 'primereact/checkbox'

// styles
import style from '@aytm/app/stats/page/sidebar/export/styles/ExportPanel.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const COOKIE_PREFIX = 'AYTM_SURVEY_EXPORT'

const INCLUDE_OPTIONS = [
  { key: 'links-chb-2', text: 'Open link to this report', default: true },
  { key: 'links-chb-4', text: 'Methodology slide(s)', default: true },
  { key: 'links-chb-3', text: 'New survey link', default: true },
  { key: 'links-chb-5', text: 'Survey Images', default: true },
  { key: 'links-chb-1', text: 'Survey preview link', default: false }
]

const Presentation = ({
  survey,
  model,
  report,
  features,
  isOeExists,
  processing,
  disabled,
  onExport,
  onParamsChange
}) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const [exportFormat, setExportFormat] = useState(
    cookie.load(`${COOKIE_PREFIX}_format-${PageGlobals.TEAM_ID}-${PageGlobals.ACCOUNT_ID}`) ||
      sidebarState.export.presentation.exportFormat ||
      'pptx'
  )

  const [includeOptions, setIncludeOptions] = useState(
    INCLUDE_OPTIONS.reduce((acc, cur) => {
      const value = cookie.load(`${COOKIE_PREFIX}_${cur.key}`)
      acc[cur.key] = value == undefined ? cur.default : 'true' == value
      return acc
    }, {})
  )

  const [clExportVisibility, setClExportVisibility] = useState('hide' == model.clExportVisibility)
  const [ignoreTags, setIgnoreTags] = useState(model.ignoreTagsExport)

  const [applyFilters, setApplyFilters] = useApplyFilters(sidebarState.export?.presentation, model.filtersPattern)
  const [dataQualityExport, setDataQualityExport] = useState(
    Object.hasOwn(sidebarState, 'export') && Object.hasOwn(sidebarState.export.presentation, 'dataQualityExport')
      ? sidebarState.export.presentation.dataQualityExport
      : model.dataQualityExport
  )

  const [exportTemplate, setExportTemplate] = useState(
    0 < PageGlobals.CUSTOM_TEMPLATES.length
      ? cookie.load(`${COOKIE_PREFIX}_template-${PageGlobals.TEAM_ID}-${PageGlobals.ACCOUNT_ID}`) || 'template-aytm'
      : null
  )

  const onIncludeOptionChange = (key, value) => {
    const date = new Date()
    date.setDate(date.getDate() + 180)

    cookie.save(`${COOKIE_PREFIX}_${key}`, String(value), { path: '/', expires: date })

    setIncludeOptions(Object.assign({}, includeOptions, { [key]: value }))
  }

  const onFormatChange = value => {
    const date = new Date()
    date.setDate(date.getDate() + 180)

    cookie.save(`${COOKIE_PREFIX}_format-${PageGlobals.TEAM_ID}-${PageGlobals.ACCOUNT_ID}`, value, {
      path: '/',
      expires: date
    })

    setExportFormat(value)
  }

  const onTemplateChange = value => {
    const date = new Date()
    date.setDate(date.getDate() + 180)

    cookie.save(`${COOKIE_PREFIX}_template-${PageGlobals.TEAM_ID}-${PageGlobals.ACCOUNT_ID}`, value, {
      path: '/',
      expires: date
    })

    setExportTemplate(value)
  }

  const onExportClick = useCallback(
    event => {
      'function' == typeof onExport &&
        onExport({
          fileType: 'pptx',
          exportType: 'summary',
          props: { noCache: Boolean(event.shiftKey), clExportVisibility, applyFilters },
          options: {
            pptx_export_options: {
              links: `${Number(includeOptions['links-chb-1'])}${Number(includeOptions['links-chb-2'])}${Number(
                includeOptions['links-chb-3']
              )}`,
              methodologies: includeOptions['links-chb-4'],
              pptxImages: includeOptions['links-chb-5'],
              template:
                0 < PageGlobals.CUSTOM_TEMPLATES.length
                  ? PageGlobals.CUSTOM_TEMPLATES.find(item => item.id == exportTemplate)?.path
                  : 'aytm_template.pptx',
              presentationFormat: exportFormat || 'pptx',
              xpertReporter: ''
            },
            ignoreTags,
            dataQualityExport
          }
        })
    },
    [
      exportFormat,
      exportTemplate,
      includeOptions,
      clExportVisibility,
      applyFilters,
      ignoreTags,
      dataQualityExport,
      onExport
    ]
  )

  useEffect(() => {
    if (0 == PageGlobals.CUSTOM_TEMPLATES.length) return
    if ('template-aytm' == exportTemplate || PageGlobals.CUSTOM_TEMPLATES.some(item => item.id == exportTemplate))
      return

    onTemplateChange('template-aytm') // stale cookie referencing a deleted template → reset to aytm default
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'export',
        state: {
          presentation: {
            ...sidebarState.export.presentation,
            exportFormat,
            applyFilters,
            // the filter the box was set against, so a later open keeps a hand-set tick only under it
            filtersPattern: model.filtersPattern,
            dataQualityExport
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exportFormat, applyFilters, model.filtersPattern, dataQualityExport]
  )

  useNonInitialEffect(
    () =>
      onParamsChange(
        {
          cl_export_visibility: clExportVisibility ? 'hide' : 'show',
          ignore_tags_export: ignoreTags
        },
        false
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clExportVisibility, ignoreTags, onParamsChange]
  )

  // useEffect(() => {}, [sidebarState.export.presentation])

  return (
    <>
      <div className={style.section}>
        <div className={style['section-title']}>
          <b>File Types</b>
        </div>

        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-5`}>
            <RadioButton
              data-testid="stat-exports-presentation-pptx"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-presentation-pptx-label"
              value="pptx"
              checked={'pptx' == exportFormat}
              disabled={disabled}
              onChange={disabled ? null : () => onFormatChange('pptx')}
            />
            <span
              data-testid="stat-exports-presentation-pptx-label"
              id="stat-exports-presentation-pptx-label"
              className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
              onClick={disabled ? null : () => onFormatChange('pptx')}
            >
              PPTX
            </span>
          </div>
          <div className={`${style.param} p-col-5`}>
            <RadioButton
              data-testid="stat-exports-presentation-pdf"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-presentation-pdf-label"
              value="pdf"
              checked={'pdf' == exportFormat}
              disabled={disabled}
              onChange={disabled ? null : () => onFormatChange('pdf')}
            />
            <span
              data-testid="stat-exports-presentation-pdf-label"
              id="stat-exports-presentation-pdf-label"
              className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
              onClick={disabled ? null : () => onFormatChange('pdf')}
            >
              PDF
            </span>
          </div>
        </div>
        <div className={`${style.row} ${style['row-indent']}`}>
          <div className={`${style.param} p-col-5`}>
            <RadioButton
              data-testid="stat-exports-presentation-key"
              className="p-radiobutton-sm"
              aria-labelledby="stat-exports-presentation-key-label"
              value="key"
              checked={'key' == exportFormat}
              disabled={disabled}
              onChange={disabled ? null : () => onFormatChange('key')}
            />
            <span
              data-testid="stat-exports-presentation-key-label"
              id="stat-exports-presentation-key-label"
              className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
              onClick={disabled ? null : () => onFormatChange('key')}
            >
              Keynote
            </span>
          </div>
        </div>
      </div>

      <div className={style.section}>
        <div className={style['section-title']}>
          <b>Include</b>
        </div>

        {INCLUDE_OPTIONS.map(option => (
          <div key={option.key} className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid={`stat-export-presentation-${option.key}`}
                className="p-checkbox-sm"
                aria-labelledby={`stat-export-presentation-${option.key}-label`}
                value={option.key}
                checked={includeOptions[option.key]}
                disabled={disabled}
                onChange={disabled ? null : () => onIncludeOptionChange(option.key, !includeOptions[option.key])}
                onKeyDown={
                  disabled
                    ? null
                    : event =>
                        toggleCheckbox(event, () => onIncludeOptionChange(option.key, !includeOptions[option.key]))
                }
              />
              <span
                data-testid={`stat-export-presentation-${option.key}-label`}
                id={`stat-export-presentation-${option.key}-label`}
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => onIncludeOptionChange(option.key, !includeOptions[option.key])}
              >
                {option.text}
              </span>
            </div>
          </div>
        ))}
        {survey?.cl_exists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-presentation-cl-export-visibility"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-presentation-cl-export-visibility-label"
                value="cl-export-visibility"
                checked={clExportVisibility}
                disabled={disabled}
                onChange={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setClExportVisibility(!clExportVisibility))
                }
              />
              <span
                data-testid="stat-exports-presentation-cl-export-visibility-label"
                id="stat-exports-presentation-cl-export-visibility-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setClExportVisibility(!clExportVisibility)}
              >
                Hide aytm Logic
              </span>
            </div>
          </div>
        ) : null}
        {0 < model.filtersPattern.length ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-presentation-apply-filters"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-presentation-apply-filters-label"
                value="apply-filters"
                checked={applyFilters}
                disabled={disabled}
                onChange={disabled ? null : () => setApplyFilters(!applyFilters)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setApplyFilters(!applyFilters))}
              />
              <span
                data-testid="stat-exports-presentation-apply-filters-label"
                id="stat-exports-presentation-apply-filters-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setApplyFilters(!applyFilters)}
              >
                Apply active filters
              </span>
            </div>
          </div>
        ) : null}
        {isOeExists ? (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-presentation-ignore-tags"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-presentation-ignore-tags-label"
                value="ignore-tags"
                checked={ignoreTags}
                disabled={disabled}
                onChange={disabled ? null : () => setIgnoreTags(!ignoreTags)}
                onKeyDown={disabled ? null : event => toggleCheckbox(event, () => setIgnoreTags(!ignoreTags))}
              />
              <span
                data-testid="stat-exports-presentation-ignore-tags-label"
                id="stat-exports-presentation-ignore-tags-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setIgnoreTags(!ignoreTags)}
              >
                Exclude OE Code Columns
              </span>
            </div>
          </div>
        ) : null}
        {!PageGlobals.USER_ID ||
        (PageGlobals.SHARE_KEY != undefined && PageGlobals.SHARE_KEY.key != undefined) ||
        features.data_quality_export.disabled ? null : (
          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <Checkbox
                data-testid="stat-exports-presentation-data-quality-export"
                className="p-checkbox-sm"
                aria-labelledby="stat-exports-presentation-data-quality-export-label"
                value="data-quality-export"
                checked={dataQualityExport}
                disabled={disabled}
                onChange={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
                onKeyDown={
                  disabled ? null : event => toggleCheckbox(event, () => setDataQualityExport(!dataQualityExport))
                }
              />
              <span
                data-testid="stat-exports-presentation-data-quality-export-label"
                id="stat-exports-presentation-data-quality-export-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => setDataQualityExport(!dataQualityExport)}
              >
                Data Quality Export
              </span>
            </div>
          </div>
        )}
      </div>

      {0 < PageGlobals.CUSTOM_TEMPLATES.length ? (
        <div className={style.section}>
          <div className={style['section-title']}>
            <b>Presentation Template</b>
          </div>

          <div className={`${style.row} ${style['row-indent']}`}>
            <div className={`${style.param} p-col-12`}>
              <RadioButton
                data-testid="stat-exports-presentation-template-aytm"
                className="p-radiobutton-sm"
                aria-labelledby="stat-exports-presentation-template-aytm-label"
                value="template-aytm"
                checked={'template-aytm' == exportTemplate}
                disabled={disabled}
                onChange={disabled ? null : () => onTemplateChange('template-aytm')}
              />
              <span
                data-testid="stat-exports-presentation-show-counts-label"
                id="stat-exports-presentation-template-aytm-label"
                className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                onClick={disabled ? null : () => onTemplateChange('template-aytm')}
              >
                aytm
              </span>
            </div>
          </div>
          {PageGlobals.CUSTOM_TEMPLATES.map(template => (
            <div key={template.id} className={`${style.row} ${style['row-indent']}`}>
              <div className={`${style.param} p-col-12`}>
                <RadioButton
                  data-testid={`stat-export-presentation-${template.id}`}
                  className="p-radiobutton-sm"
                  aria-labelledby={`stat-export-presentation-${template.id}-label`}
                  value={template.id}
                  checked={template.id == exportTemplate}
                  disabled={disabled}
                  onChange={disabled ? null : () => onTemplateChange(template.id)}
                />
                <span
                  data-testid="stat-exports-presentation-show-percentages-label"
                  id={`stat-export-presentation-${template.id}-label`}
                  className={classNames(style['row-label'], style['row-label-sm'], { 'p-disabled': disabled })}
                  onClick={disabled ? null : () => onTemplateChange(template.id)}
                >
                  {template.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className={style.section}>
        <div className={style.row}>
          <Button
            data-testid="stat-exports-presentation-export"
            className="p-button-rounded p-button-blue"
            label={'presentation' == processing ? 'Downloading' : 'Download file'}
            loading={'presentation' == processing}
            disabled={null !== processing || disabled}
            onClick={null !== processing || disabled ? null : onExportClick}
          />
        </div>

        {!report.current.schema.length ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 question in order to use these features
          </div>
        ) : !model.totals.responses && !model.loading ? (
          <div className={`${style.row} ${style['row-error']}`}>
            You need at least 1 respondent in order to use these features
          </div>
        ) : null}
      </div>
    </>
  )
}

Presentation.propTypes = {
  survey: PropTypes.object,
  model: PropTypes.object,
  report: PropTypes.object,
  features: PropTypes.object,
  filteredQuestions: PropTypes.array,
  isOeExists: PropTypes.bool,
  processing: PropTypes.string,
  disabled: PropTypes.bool,
  onExport: PropTypes.func,
  onParamsChange: PropTypes.func
}

Presentation.displayName = 'Presentation'

export default Presentation
